import { useEffect, useState } from 'react';
import {
  DropdownLink,
  DropdownList,
  DropdownTrigger,
} from '@spotify-internal/encore-web';
import {
  StyledDropdown,
  StyledFormGroup,
} from '@components/Catalog/ContentArea/CatalogExports/styles';

export interface DropdownItem {
  text: string;
  id: string;
}

export interface FormFieldDropdownProps {
  label: string;
  id: string;
  name: string;
  items: DropdownItem[];
  optional?: boolean;
  onChange: (id: string) => void;
}

export const FormFieldDropdown = ({
  label,
  id,
  name,
  items,
  optional = false,
  onChange,
}: FormFieldDropdownProps) => {
  const dropdownListId = `${id}-dropdown-list`;
  const triggerId = `${id}-trigger`;

  const [show, setShow] = useState<boolean>(false);
  const [selected, setSelected] = useState<number>(0);

  useEffect(() => {
    if (show) {
      const element = document.getElementById(dropdownListId) as HTMLElement;
      element.focus();
    }
  }, [show, dropdownListId]);

  const handleClick = (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    setShow(!show);
    setSelected(index);
    onChange(items[index].id);
  };

  const onHide = () => {
    setShow(false);
    const element = document.getElementById(triggerId) as HTMLElement;
    element.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const upArrow = 'ArrowUp';
    const downArrow = 'ArrowDown';
    const enter = 'Enter';
    const escape = 'Escape';
    const tab = 'Tab';
    const space = ' ';

    switch (e.key) {
      case upArrow: {
        e.preventDefault();
        if (selected !== 0) {
          onChange(items[selected - 1].id);
          setSelected(selected - 1);
        }
        break;
      }

      case downArrow: {
        e.preventDefault();
        if (selected < items.length - 1) {
          onChange(items[selected + 1].id);
          setSelected(selected + 1);
        }
        break;
      }

      case enter:
      case escape:
      case space:
      case tab: {
        e.preventDefault();
        setShow(false);
        const element = document.getElementById(triggerId) as HTMLElement;
        element.focus();
        break;
      }

      default: {
        break;
      }
    }
  };

  return (
    <StyledFormGroup
      indicator={optional ? 'optional' : undefined}
      label={label}
      labelFor={triggerId}
    >
      <DropdownTrigger
        name={name}
        overlay={
          show && (
            <DropdownList
              onKeyDown={e => onKeyDown(e)}
              onBlur={() => setShow(false)}
              id={dropdownListId}
              name={name}
              data-testid={dropdownListId}
              aria-activedescendant={items[selected].id}
            >
              {items.map((item, index) => (
                <DropdownLink
                  type="li"
                  as="li"
                  role="option"
                  id={item.id}
                  name={item.id}
                  key={item.id}
                  onFocus={() => setSelected(index)}
                  onClick={e => handleClick(e, index)}
                  selected={items[selected].id === item.id}
                  aria-selected={items[selected].id === item.id}
                >
                  {item.text}
                </DropdownLink>
              ))}
            </DropdownList>
          )
        }
        onShow={() => setShow(true)}
        onHide={onHide}
        data-testid={triggerId}
      >
        <StyledDropdown
          name={name}
          id={triggerId}
          type="button"
          aria-labelledby={`dropdown-label ${triggerId}`}
          aria-expanded={show || undefined}
        >
          {items[selected].text}
        </StyledDropdown>
      </DropdownTrigger>
    </StyledFormGroup>
  );
};
